// ChatWidget.js — Asistente Hidrometrics (flotante, solo administradores)
// Se monta una vez por pantalla (hoy: DashboardA). El widget se oculta solo
// si el usuario no es administrador o el backend no emitió chat_token.
import React, { useContext, useEffect, useRef, useState } from "react";
import { AuthContext } from "../AuthContext/AuthContext";
import "./ChatWidget.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;
const STORE_KEY = "hm_chat_msgs_v1";
const MAX_INPUT = 400; // se ajusta con /api/chat/status

const SUGGESTIONS = [
  "Estado de mis escuelas esta semana",
  "Reporte en Excel de los hoteles",
  "¿Qué significa el color del tanque?",
];

const WELCOME = {
  role: "assistant",
  text: "Hola; soy Hydro AI, tu asistente. Puedo ayudarte a entender la plataforma y generar reportes.",
};

// Render seguro (sin HTML): soporta **negritas** y viñetas "* " / "- " / "• ".
const renderInline = (line, keyBase) =>
  line.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**") ? (
      <strong key={`${keyBase}-${i}`}>{part.slice(2, -2)}</strong>
    ) : (
      <React.Fragment key={`${keyBase}-${i}`}>{part}</React.Fragment>
    ),
  );

const renderText = (text) =>
  String(text)
    .split("\n")
    .map((raw, i) => {
      const bullet = /^\s*([*\-•])\s+(.*)$/.exec(raw);
      return bullet ? (
        <div className="cw-li" key={i}>
          <span className="cw-dot">•</span>
          <span>{renderInline(bullet[2], i)}</span>
        </div>
      ) : (
        <div key={i}>{raw.trim() ? renderInline(raw, i) : <br />}</div>
      );
    });

const loadStored = () => {
  try {
    const raw = sessionStorage.getItem(STORE_KEY);
    const arr = raw ? JSON.parse(raw) : null;
    return Array.isArray(arr) && arr.length ? arr : [WELCOME];
  } catch {
    return [WELCOME];
  }
};

const ChatWidget = () => {
  const { user } = useContext(AuthContext);
  const token = user?.chat_token;
  const enabled = Number(user?.type) === 0 && !!token;

  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState(loadStored);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [maxInput, setMaxInput] = useState(MAX_INPUT);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    try {
      sessionStorage.setItem(STORE_KEY, JSON.stringify(msgs.slice(-40)));
    } catch {
      /* sin almacenamiento: el chat funciona igual */
    }
  }, [msgs]);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [msgs, busy, open]);

  useEffect(() => {
    if (!open || !enabled) return;
    inputRef.current?.focus();
    fetch(`${API_BASE}/api/chat/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => d?.maxInput && setMaxInput(d.maxInput))
      .catch(() => {});
  }, [open, enabled, token]);

  if (!enabled) return null;

  const push = (m) => setMsgs((prev) => [...prev, m]);

  const send = async (textArg) => {
    const text = (textArg ?? input).trim();
    if (!text || busy) return;
    setInput("");
    push({ role: "user", text });
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/chat/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: text }),
      });
      if (res.status === 403) {
        push({ role: "assistant", text: "Tu sesión del asistente expiró. Vuelve a iniciar sesión para continuar.", error: true });
      } else {
        const data = await res.json().catch(() => null);
        if (data?.reply) push({ role: "assistant", text: data.reply, report: data.report, error: !!data.degraded });
        else push({ role: "assistant", text: "El asistente no está disponible en este momento. Por favor contacta a soporte.", error: true });
      }
    } catch {
      push({ role: "assistant", text: "No pude contactar al servidor. Por favor contacta a soporte si el problema continúa.", error: true });
    } finally {
      setBusy(false);
    }
  };

  const downloadReport = async (report) => {
    try {
      const res = await fetch(`${API_BASE}/api/chat/report/${report.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        push({ role: "assistant", text: d.message || "No se pudo descargar el reporte.", error: true });
        return;
      }
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `Reporte_Hidrometrics.${report.formato}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 5000);
    } catch {
      push({ role: "assistant", text: "No se pudo descargar el reporte. Intenta de nuevo.", error: true });
    }
  };

  const reset = () => {
    setMsgs([WELCOME]);
    fetch(`${API_BASE}/api/chat/reset`, { method: "POST", headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {!open && (
        <button type="button" className="cw-fab" onClick={() => setOpen(true)} aria-label="Abrir asistente">
          💬
        </button>
      )}

      {open && (
        <div className="cw-panel" role="dialog" aria-label="Asistente Hidrometrics">
          <div className="cw-head">
            <div className="cw-title">
              <span className="cw-title-ico">💧</span> Hydro AI
            </div>
            <div className="cw-head-btns">
              <button type="button" onClick={reset} title="Nueva conversación" aria-label="Nueva conversación">↺</button>
              <button type="button" onClick={() => setOpen(false)} title="Cerrar" aria-label="Cerrar">✕</button>
            </div>
          </div>

          <div className="cw-body">
            {msgs.map((m, i) => (
              <div key={i} className={`cw-msg cw-msg--${m.role}${m.error ? " cw-msg--error" : ""}`}>
                <div className="cw-bubble">{renderText(m.text)}</div>
                {m.report && (
                  <button type="button" className="cw-report" onClick={() => downloadReport(m.report)}>
                    ⬇ Descargar {m.report.formato === "pdf" ? "PDF" : "Excel"} ({m.report.instalaciones} instalación
                    {m.report.instalaciones === 1 ? "" : "es"})
                  </button>
                )}
              </div>
            ))}
            {busy && (
              <div className="cw-msg cw-msg--assistant">
                <div className="cw-bubble cw-typing"><span /><span /><span /></div>
              </div>
            )}
            {msgs.length <= 1 && !busy && (
              <div className="cw-chips">
                {SUGGESTIONS.map((s) => (
                  <button key={s} type="button" onClick={() => send(s)}>{s}</button>
                ))}
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          <div className="cw-foot">
            <textarea
              ref={inputRef}
              rows={1}
              value={input}
              maxLength={maxInput}
              placeholder="Escribe tu pregunta..."
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              disabled={busy}
            />
            <button type="button" className="cw-send" onClick={() => send()} disabled={busy || !input.trim()} aria-label="Enviar">
              ➤
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;